import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DisplayModus } from 'constants/constants'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronUp, faChevronDown, faTrash } from '@fortawesome/free-solid-svg-icons'
import { faSquarePlus } from '@fortawesome/free-regular-svg-icons'
import {
  Collapsible,
  IconButton,
  Inline,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@airbus/components-react/components'

import useMutation from 'utils/hooks/useMutation'
import LocalStorage from 'utils/myLocalStorage'
import URL_APIS from 'utils/api/urlApis'
import ModalConfirm from 'components/Modal/ModalConfim'
import { UsedTool, UsedToolInput } from 'types/workorder/usedTool'
import ModalUsedTools from './modalUsedTools'
import { Lang } from 'context/LangContext'
import ComponentsUNames from 'constants/componentsUNames'
import Fieldset from 'components/Fieldset/fieldset'
import urlApis from 'utils/api/urlApis'
import useQuery from 'utils/hooks/useQuery'
import { RequiredToolInput } from 'types/workorder/requiredTool'

type Props = {
  lang: Lang
  oenumdoc: string | null
  oeType: string | undefined
  selectedResource: RequiredToolInput
  displayMode: DisplayModus
  expandAll: boolean
  usedTools: never[]
  setUsedTools: React.Dispatch<React.SetStateAction<never[]>>
}

type SelectedUsedTool = UsedTool & {
  checked?: boolean
  open?: boolean
  loading?: boolean
  errorMsg?: string
  done?: boolean
  date?: Date
  resourceType?: string
}

const WoUsedTools = ({
  lang,
  oenumdoc,
  oeType,
  selectedResource,
  displayMode,
  expandAll,
  usedTools,
  setUsedTools,
}: Props) => {
  const { t } = useTranslation()

  const [isModalFormOpen, setIsModalFormOpen] = useState<boolean>(false)
  const [modalFormDisplayMode, setModalFormDisplayMode] = useState(DisplayModus.READ)
  const [selectedUsedTool, setSelectedUsedTool] = useState<UsedToolInput>()

  const { fetchData: fetchUsedTools } = useQuery<UsedTool[]>(urlApis.URL_WORKORDER_USED_TOOLS_GET)

  // Expand/collapse status of main component
  const [isExpanded, setIsExpanded] = useState<boolean>(false)
  // Data grouped by type: {<Type_1>: [{...}, {...}], <Type_2>: [{...}, {...}]}
  const [dataGroupedByType, setDataGroupedByType] = useState({})
  // Expand/collapse status of grouped-by type tables
  const [isExpandedGroupedByType, setIsExpandedGroupedByType] = useState({})
  const [usedToolsToDelete, setUsedToolsToDelete] = useState<SelectedUsedTool | null>(null)

  const { mutate: deleteUsedTools } = useMutation(URL_APIS.URL_WORKORDER_USED_TOOLS_DELETE)

  const canUpdate =
    LocalStorage.getOrDefault(
      [LocalStorage.KEY_PERMISSIONS, ComponentsUNames.WORKORDER_UPDATE_PERMISSIONS],
      LocalStorage.VALUE_TYPE.BOOLEAN,
      false,
    ) &&
    (oeType !== 'DOC' ||
      (oeType === 'DOC' &&
        LocalStorage.getOrDefault(
          [LocalStorage.KEY_PERMISSIONS, ComponentsUNames.WORKORDER_UPDATE_OE_SB_PERMISSIONS],
          LocalStorage.VALUE_TYPE.BOOLEAN,
          false,
        )))

  const canCreate = canUpdate

  const handleModalFormPostSubmit = () => {
    setIsModalFormOpen(false)
    setSelectedUsedTool(undefined)

    loadData()
  }

  const handleDelete = async () => {
    if (!usedToolsToDelete) return
    setUsedToolsToDelete({ ...usedToolsToDelete, loading: true, open: false })
    await deleteUsedTools({
      params: { id: usedToolsToDelete.ouid.toString() },
      method: 'DELETE',
    })

    // await setTimeout(() => {
    setUsedToolsToDelete(null)
    loadData()
    // }, 2000)
  }

  const handleCreateUsedTool = () => {
    setSelectedUsedTool({
      ounumdoc1: oenumdoc?.substring(0, 4),
      ounumdoc2: oenumdoc?.substring(4),
      codeType: '',
      libType: '',
      pn: '',
      pnTypeCode: '',
      sn: '',
      designation: '',
      echeance: undefined,
    })
    setModalFormDisplayMode(DisplayModus.CREATE)
    setIsModalFormOpen(true)
  }

  const loadData = async () => {
    if (oenumdoc) {
      const response = await fetchUsedTools({
        lang: lang,
        oenumdoc1: oenumdoc?.substring(0, 4),
        oenumdoc2: oenumdoc?.substring(4),
      })
      setUsedTools(response?.data?.content)
    }
  }

  /**
   * Load required resources of Work-order
   */
  useEffect(() => {
    if (isExpanded) {
      loadData()
    } else {
      setUsedTools([])
    }
  }, [lang, isExpanded, selectedResource])

  /**
   * Group by data (required resources) by type (keep ordering as it is)
   */
  useEffect(() => {
    if (usedTools) {
      const dataGroupByType = {}
      const isExpandedGroupByType = {}
      usedTools.forEach((objResource) => {
        if (Object.prototype.hasOwnProperty.call(dataGroupByType, objResource['libType'])) {
          dataGroupByType[objResource['libType']].push(objResource)
        } else {
          dataGroupByType[objResource['libType']] = [objResource]
          isExpandedGroupByType[objResource['libType']] = isExpanded
        }
      })
      setDataGroupedByType(dataGroupByType)
      setIsExpandedGroupedByType(isExpandedGroupByType)
    } else {
      setDataGroupedByType({})
      setIsExpandedGroupedByType({})
    }
  }, [usedTools])

  /**
   * ExpandAll and collapseAll are mirror-toggled, so a hook on expandAll is enough
   */
  useEffect(() => {
    setIsExpanded(expandAll)

    if (JSON.stringify(isExpandedGroupedByType) !== '{}') {
      const copyIsExpandedGroupedByType = { ...isExpandedGroupedByType }
      Object.keys(copyIsExpandedGroupedByType).map((it) => (copyIsExpandedGroupedByType[it] = expandAll))
      setIsExpandedGroupedByType(copyIsExpandedGroupedByType)
    }
  }, [expandAll])

  const handleSubSectionToggleExpandCollapse = (evt) => {
    const actionName = evt.currentTarget.attributes['name'].nodeValue
    const copyIsExpandedGroupedByType = { ...isExpandedGroupedByType }
    copyIsExpandedGroupedByType[actionName] = !isExpandedGroupedByType[actionName]
    setIsExpandedGroupedByType(copyIsExpandedGroupedByType)
  }

  return (
    <div style={{ width: '100%' }}>
      <Collapsible title={t('9300198')} expanded={isExpanded} onChange={() => setIsExpanded(!isExpanded)}>
        <div>
          {canCreate && (
            <IconButton variant='ghost' size='small' onClick={handleCreateUsedTool} style={{ marginBottom: '1em' }}>
              <FontAwesomeIcon icon={faSquarePlus} />
            </IconButton>
          )}
        </div>
        <div>
          {JSON.stringify(dataGroupedByType) !== '{}' && JSON.stringify(dataGroupedByType) !== '[]' ? (
            <div>
              {Object.keys(dataGroupedByType).map((resourceType, i) => (
                <Fieldset
                  legend={
                    <>
                      {isExpandedGroupedByType[resourceType] ? (
                        <FontAwesomeIcon
                          name={resourceType}
                          icon={faChevronUp}
                          onClick={handleSubSectionToggleExpandCollapse}
                        />
                      ) : (
                        <FontAwesomeIcon
                          name={resourceType}
                          icon={faChevronDown}
                          onClick={handleSubSectionToggleExpandCollapse}
                        />
                      )}
                      {resourceType}
                    </>
                  }
                  key={'resourcesUsedType_' + i}
                >
                  <div
                    style={{
                      display: isExpandedGroupedByType[resourceType] ? 'inherit' : 'none',
                    }}
                  >
                    <Table>
                      <colgroup>
                        <col style={{ width: '5%' }} />
                        <col style={{ width: '5%' }} />
                        <col style={{ width: '5%' }} />
                        <col style={{ width: '5%' }} />
                        <col style={{ width: '2%' }} />
                      </colgroup>
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('9500047')}</TableCell>
                          <TableCell>{t('9500048')}</TableCell>
                          <TableCell>{t('7000248')}</TableCell>
                          <TableCell>{t('1MOE030')}</TableCell>
                          <TableCell />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {dataGroupedByType[resourceType].map((objResource, j) => (
                          <TableRow key={'resourcesUsedTypeRow_' + i + '_' + j}>
                            <TableCell>
                              {Object.prototype.hasOwnProperty.call(objResource, 'pn') ? objResource['pn'] : '-'}
                            </TableCell>
                            <TableCell>
                              {Object.prototype.hasOwnProperty.call(objResource, 'sn') ? objResource['sn'] : '-'}
                            </TableCell>
                            <TableCell>
                              {Object.prototype.hasOwnProperty.call(objResource, 'designation')
                                ? objResource['designation']
                                : '-'}
                            </TableCell>
                            <TableCell>
                              {Object.prototype.hasOwnProperty.call(objResource, 'echeance')
                                ? objResource.echeance
                                : '-'}
                            </TableCell>
                            <TableCell>
                              {canUpdate && (
                                <Inline>
                                  <IconButton
                                    size='small'
                                    onClick={() => setUsedToolsToDelete({ ...objResource, open: true })}
                                    variant='ghost'
                                  >
                                    {objResource?.pn === usedToolsToDelete?.pn && usedToolsToDelete?.loading ? (
                                      <Spinner size='small' />
                                    ) : (
                                      <FontAwesomeIcon size='xs' icon={faTrash} />
                                    )}
                                  </IconButton>
                                </Inline>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </Fieldset>
              ))}
            </div>
          ) : (
            <div style={{ paddingLeft: '1em' }}>{t('9500055')}</div>
          )}
        </div>
      </Collapsible>
      <ModalConfirm
        isOpened={Boolean(usedToolsToDelete?.open)}
        onCloseModal={() => setUsedToolsToDelete(null)}
        confirmMessage={t('9510089')}
        onSubmit={handleDelete}
      />
      <ModalUsedTools
        lang={lang}
        open={isModalFormOpen}
        displayMode={modalFormDisplayMode}
        onClose={() => setIsModalFormOpen(false)}
        usedTool={selectedUsedTool}
        onPostSubmit={handleModalFormPostSubmit}
      />
    </div>
  )
}

export default WoUsedTools
